/**
 * Voice transcription from the chat: pick an audio file, upload it, follow
 * the transcription task, then hand off to the analysis that follows it
 * (started by the server, or by us with default ICF options), and report
 * the result back when an AI tool call asked for the recording.
 */
import { useCallback, useRef, useState, type MutableRefObject } from 'react';
import { Platform } from 'react-native';
import * as DocumentPicker from 'expo-document-picker';

import { showDialog } from '@/utils/dialogs';
import { audioFileTooLargeParams } from '../../config/uploadLimits';
import { taskAPI, transcriptionAPI } from '../../services/api';
import { userFacingErrorMessage } from '../../services/errorMessages';
import { derivedIdempotencyKey, useIdempotencyIntent } from '../../services/idempotencyIntent';
import { openWebFilePicker } from '../../services/webFilePicker';
import type {
  AnalysisOptions,
  AudioFileInfo,
  ToolResult,
  TranscriptionOptions,
  TranscriptionStatusResponse,
  UnifiedAIResponse,
} from '../../types';
import { noCreditMessage } from '../../utils/creditErrors';
import { trackAuthEvent } from '../../services/analytics';
import {
  audioUploadRejectionMessage,
  transcriptionFailureEvent,
  voiceMinutesExceededMessage,
} from '../../utils/transcribeError';
import { trialAnalysisUsedMessage } from '../../utils/trialAnalysisUsed';
import { DEFAULT_AUDIO_MAX_MB, type PendingTask } from './chatConstants';
import { AUDIO_UPLOAD_MIME_TYPES, AUDIO_UPLOAD_WEB_ACCEPT } from '../../utils/audioUploadFormats';
import type {
  LoadMessages,
  LoadSessions,
  Loader,
  SessionSwitching,
  SetState,
  TaskCompletion,
  TFn,
  TrialState,
} from './chatFlowTypes';
import type { ConversationGuard } from './conversationGuard';
import type { ChatOperationRefs } from './useChatOperationRefs';

type AnalysisRetry = ((idempotencyKey: string) => Promise<{ task_id: string; session_id?: number | null }>) | null;

export function useVoiceTranscription({
  ops,
  guard,
  language,
  t,
  currentSessionId,
  canIncludeInIcfProgress,
  audioUploadLimits,
  wsUnavailable,
  transcriptBlocked,
  showTranscriptLimitWarning,
  showSession,
  adoptSession,
  loadMessages,
  loadSessions,
  loadQuotaStatus,
  loadTranscriptionUsage,
  loadRecentDocuments,
  waitForTaskResult,
  trialStatus,
  refreshTrialStatus,
  addPendingTask,
  removePendingTask,
  markSessionAnalyzed,
  setAnalyzingFile,
  setViewingTranscript,
  submitToolResults,
  reportAudioPickCancelled,
  analysisProgress,
}: SessionSwitching & {
  ops: ChatOperationRefs;
  guard: ConversationGuard;
  language: string;
  t: TFn;
  currentSessionId: number | null;
  canIncludeInIcfProgress: boolean;
  audioUploadLimits: { max_size_mb?: number | null } | null | undefined;
  wsUnavailable: boolean;
  transcriptBlocked: boolean;
  showTranscriptLimitWarning: () => void;
  loadMessages: LoadMessages;
  loadSessions: LoadSessions;
  loadQuotaStatus: Loader;
  loadTranscriptionUsage: Loader;
  loadRecentDocuments: Loader;
  waitForTaskResult: TaskCompletion['waitForTaskResult'];
  trialStatus: TrialState['trialStatus'];
  refreshTrialStatus: TrialState['refreshTrialStatus'];
  addPendingTask: (task: PendingTask) => void;
  removePendingTask: (taskId: string) => void;
  markSessionAnalyzed: (sessionId: number | null | undefined) => void;
  setAnalyzingFile: SetState<boolean>;
  setViewingTranscript: SetState<any>;
  submitToolResults: (
    toolResults: ToolResult[],
    options?: {
      originIntent?: string | null;
      documentId?: number;
      analysisInstructions?: string;
      includeInICFProgress?: boolean;
    }
  ) => Promise<void>;
  reportAudioPickCancelled: () => Promise<void>;
  /** The AnalysisProgress modal state the analysis hook owns; this flow drives it after transcription. */
  analysisProgress: {
    setShowAnalysisProgress: SetState<boolean>;
    setAnalysisTaskId: SetState<string | null>;
    setAnalysisSessionId: SetState<number | null>;
    setAnalyzingDocumentName: SetState<string | null>;
    setAnalysisRetryAvailable: SetState<boolean>;
    analysisRetryRef: MutableRefObject<AnalysisRetry>;
    analysisAutoOpenRef: MutableRefObject<boolean>;
    analysisCompletionHandledRef: MutableRefObject<boolean>;
  };
}) {
  const {
    activeOperationSessionRef,
    cancelledTaskIdsRef,
    pendingToolIntentRef,
    pendingToolResultsRef,
    pendingVoiceToolParamsRef,
    pendingVoiceToolRef,
    pickAudioForTranscriptionRef,
  } = ops;
  const {
    setShowAnalysisProgress,
    setAnalysisTaskId,
    setAnalysisSessionId,
    setAnalyzingDocumentName,
    setAnalysisRetryAvailable,
    analysisRetryRef,
    analysisAutoOpenRef,
    analysisCompletionHandledRef,
  } = analysisProgress;

  // Voice Transcription states
  const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);
  const [showTranscriptionProgress, setShowTranscriptionProgress] = useState(false);
  const [pendingAudioFile, setPendingAudioFile] = useState<AudioFileInfo | null>(null);
  const [transcriptionStatus, setTranscriptionStatus] = useState<TranscriptionStatusResponse | null>(null);
  const [transcribingDocumentId, setTranscribingDocumentId] = useState<number | null>(null);
  const [transcriptionEstimate, setTranscriptionEstimate] = useState<{
    seconds?: number | null;
    minSeconds?: number | null;
    maxSeconds?: number | null;
  } | null>(null);

  const [transcriptionTaskId, setTranscriptionTaskId] = useState<string | null>(null);
  const cancelPendingTranscriptionRef = useRef(false);
  // ASYNC-6: same picked file => same Idempotency-Key (see useDocumentAnalysis).
  const transcriptionIntent = useIdempotencyIntent();

  const isTaskCancellationError = useCallback((error: any, ...taskIds: Array<string | null | undefined>) => {
    const message = String(error?.message || error || '').toLowerCase();
    return message.includes('cancel') || taskIds.some((id) => Boolean(id && cancelledTaskIdsRef.current.has(id)));
  }, [cancelledTaskIdsRef]);

  const handleTranscriptionProgressCancel = (taskId?: string | null) => {
    if (taskId) {
      cancelledTaskIdsRef.current.add(taskId);
    } else {
      cancelPendingTranscriptionRef.current = true;
    }
    setShowTranscriptionProgress(false);
    setTranscriptionTaskId(null);
    setTranscribingDocumentId(null);
    setTranscriptionStatus(null);
    setTranscriptionEstimate(null);
    setPendingAudioFile(null);
  };

  const handleAudioFileSelected = (file: { uri: string; type: string; name: string; size?: number }) => {
    const maxSizeMb = audioUploadLimits?.max_size_mb ?? DEFAULT_AUDIO_MAX_MB;
    const maxSizeBytes = maxSizeMb * 1024 * 1024;

    if (file.size && file.size > maxSizeBytes) {
      const message = t('transcription.errors.fileTooLarge', audioFileTooLargeParams(file.size, maxSizeMb));
      showDialog(message);
      return;
    }

    setPendingAudioFile({
      uri: file.uri,
      name: file.name,
      type: file.type,
      size: file.size,
    });
    setShowTranscriptionModal(true);
  };

  // Robust web fallback for audio file picking — mirrors openWebAnalysisFilePicker.
  // expo-document-picker on web sometimes ignores audio mime types depending on the
  // browser; using a plain <input type=file accept=audio/*> is more reliable.
  const openWebAudioFilePicker = useCallback(() => openWebFilePicker({
    accept: [...AUDIO_UPLOAD_WEB_ACCEPT, 'audio/*'],
    onFile: (file) => {
      const blobUrl = URL.createObjectURL(file);
      handleAudioFileSelected({
        uri: blobUrl,
        type: file.type || 'audio/mpeg',
        name: file.name || 'audio',
        size: file.size,
      });
    },
    onCancel: () => { void reportAudioPickCancelled(); },
  }), [handleAudioFileSelected, reportAudioPickCancelled]);

  const pickAudioForTranscription = useCallback(async () => {
    if (Platform.OS === ('web' as any) && openWebAudioFilePicker()) {
      return;
    }
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: AUDIO_UPLOAD_MIME_TYPES,
        copyToCacheDirectory: true,
      });

      if (result.canceled || !result.assets || result.assets.length === 0) {
        await reportAudioPickCancelled();
        return;
      }

      const asset = result.assets[0];
      handleAudioFileSelected({
        uri: asset.uri,
        type: asset.mimeType || 'audio/mpeg',
        name: asset.name || 'audio',
        size: asset.size,
      });
    } catch (error: any) {
      console.error('Audio file pick error:', error);
      const errorMessage = t('transcription.errors.uploadFailed');
      showDialog(errorMessage);
      if (pendingVoiceToolRef.current) {
        const extraResults = pendingToolResultsRef.current || [];
        const errorResult: ToolResult = {
          tool_call_id: pendingVoiceToolRef.current.tool_call_id,
          tool_name: pendingVoiceToolRef.current.tool_name,
          status: 'error',
          result: { error: errorMessage },
        };
        pendingVoiceToolRef.current = null;
        pendingVoiceToolParamsRef.current = null;
        pendingToolResultsRef.current = [];
        await submitToolResults([...extraResults, errorResult], {
          originIntent: pendingToolIntentRef.current,
        });
      }
    }
  }, [
    handleAudioFileSelected, openWebAudioFilePicker, reportAudioPickCancelled, submitToolResults, t,
    pendingToolIntentRef, pendingToolResultsRef, pendingVoiceToolParamsRef, pendingVoiceToolRef,
  ]);

  pickAudioForTranscriptionRef.current = pickAudioForTranscription;

  const handleTranscriptionCancelled = useCallback(async () => {
    setShowTranscriptionModal(false);
    setPendingAudioFile(null);
    if (pendingVoiceToolRef.current) {
      const extraResults = pendingToolResultsRef.current || [];
      const cancelledResult: ToolResult = {
        tool_call_id: pendingVoiceToolRef.current.tool_call_id,
        tool_name: pendingVoiceToolRef.current.tool_name,
        status: 'cancelled',
        result: { reason: 'user_cancelled' },
      };
      pendingVoiceToolRef.current = null;
      pendingVoiceToolParamsRef.current = null;
      pendingToolResultsRef.current = [];
      await submitToolResults([...extraResults, cancelledResult], {
        originIntent: pendingToolIntentRef.current,
      });
    }
  }, [submitToolResults, pendingToolIntentRef, pendingToolResultsRef, pendingVoiceToolParamsRef, pendingVoiceToolRef]);

  // Voice Transcription Handler
  const handleStartTranscription = async (options: TranscriptionOptions) => {
    if (!pendingAudioFile) return;
    const toolCall = pendingVoiceToolRef.current;
    const toolDefaults = pendingVoiceToolParamsRef.current;
    const originIntent = pendingToolIntentRef.current;
    const isToolDriven = Boolean(toolCall);
    let activeTranscriptionTaskId: string | null = null;
    let activeAnalysisTaskId: string | null = null;
    cancelPendingTranscriptionRef.current = false;
    if (transcriptBlocked) {
      setShowTranscriptionModal(false);
      showTranscriptLimitWarning();
      return;
    }

    setShowTranscriptionModal(false);
    setShowTranscriptionProgress(true);
    setTranscriptionStatus({ id: 0, status: 'pending' });
    // Same picked file => same key, so a double "Start" replays one upload.
    const transcriptionKey = transcriptionIntent.key(pendingAudioFile);

    try {
      console.log('🎙️ Starting transcription...', options);

      const mergedOptions: TranscriptionOptions = { ...options };
      if (toolDefaults) {
        if (toolDefaults.language) {
          mergedOptions.language = toolDefaults.language;
        }
        if (toolDefaults.apply_grammar_correction !== undefined) {
          mergedOptions.applyGrammarCorrection = toolDefaults.apply_grammar_correction;
        }
        if (toolDefaults.enable_diarization !== undefined) {
          mergedOptions.enableDiarization = toolDefaults.enable_diarization;
        }
      }

      const shouldAutoAnalyzeViaTranscription = !isToolDriven;
      const analysisInstructionsForTranscription = shouldAutoAnalyzeViaTranscription
        ? mergedOptions.analysisInstructions
        : undefined;
      const includeInICFForTranscription = shouldAutoAnalyzeViaTranscription
        ? mergedOptions.includeInICFProgress
        : undefined;
      const sessionIdForTranscription = shouldAutoAnalyzeViaTranscription && analysisInstructionsForTranscription
        ? currentSessionId ?? undefined
        : undefined;

      // Upload and start transcription
      const response = await transcriptionAPI.transcribe(
        {
          uri: pendingAudioFile.uri,
          type: pendingAudioFile.type,
          name: pendingAudioFile.name,
        },
        {
          ...mergedOptions,
          uiLanguage: language,
          sessionId: sessionIdForTranscription,
          analysisInstructions: analysisInstructionsForTranscription,
          includeInICFProgress: includeInICFForTranscription,
          idempotencyKey: transcriptionKey,
        }
      );

      console.log('📤 Transcription started:', response);
      if (trialStatus?.active && analysisInstructionsForTranscription) {
        await refreshTrialStatus();
      }
      activeTranscriptionTaskId = response.task_id;
      if (cancelPendingTranscriptionRef.current) {
        cancelledTaskIdsRef.current.add(response.task_id);
        try {
          await taskAPI.cancel(response.task_id);
        } catch (cancelError) {
          console.warn('Failed to cancel just-created transcription task:', cancelError);
        }
        setShowTranscriptionProgress(false);
        setTranscriptionTaskId(null);
        setTranscribingDocumentId(null);
        setTranscriptionEstimate(null);
        setPendingAudioFile(null);
        return;
      }
      setTranscribingDocumentId(response.document_id);
      setTranscriptionTaskId(response.task_id);
      setTranscriptionEstimate({
        seconds: response.estimated_completion_seconds,
        minSeconds: response.estimated_completion_min_seconds,
        maxSeconds: response.estimated_completion_max_seconds,
      });
      if (response.session_id) {
        activeOperationSessionRef.current = response.session_id;
        showSession(response.session_id);
        await loadSessions();
      }

      // Wait for completion via WebSocket (with polling fallback)
      // Note: WebSocket progress is handled by TranscriptionProgress component
      // We only need to wait for completion here
      let transcriptionCompletionResult: any = null;
      if (wsUnavailable) {
        // Fallback to polling
        const transcript = await transcriptionAPI.pollForCompletion(
          response.document_id,
          (status) => {
            setTranscriptionStatus(status);
          }
        );
        setTranscriptionTaskId(null);
        console.log('✅ Transcription complete (via polling):', transcript);
        setViewingTranscript(transcript);
        try {
          const taskStatus = await taskAPI.getStatus(response.task_id);
          transcriptionCompletionResult = taskStatus.result;
        } catch (statusError) {
          console.warn('Failed to load transcription task result:', statusError);
        }
      } else {
        // Use WebSocket - wait for completion then fetch transcript
        transcriptionCompletionResult = await waitForTaskResult(response.task_id, {
          onProgress: (status) => {
            setTranscriptionStatus({ id: response.document_id, status: 'processing', progress: status.progress });
          },
        });
        setTranscriptionTaskId(null);
        // Fetch the transcript content after completion
        const transcript = await transcriptionAPI.getContent(response.document_id);
        console.log('✅ Transcription complete (via WebSocket):', transcript);
        setViewingTranscript(transcript);
      }

      // Resolve the analysis task to follow. Backend may have auto-started
      // analysis during transcription (when analysisInstructions were passed);
      // otherwise — if this isn't a tool-driven flow — we kick off analysis
      // ourselves with default ICF/auto options to match Library's behavior
      // ("auto-run analysis everywhere — same flow from any entry point").
      let nextAnalysisTaskId: string | undefined = transcriptionCompletionResult?.analysis_task_id;
      let nextAnalysisSessionId: number | undefined =
        transcriptionCompletionResult?.session_id ?? response.session_id;

      const shouldAutoAnalyzeFromChat = !isToolDriven && !nextAnalysisTaskId;
      if (shouldAutoAnalyzeFromChat) {
        try {
          const defaultOptions: AnalysisOptions = {
            analysisMode: 'icf',
            requestedLevel: 'auto',
            includeMarkerTable: false,
            includeInIcfProgress: canIncludeInIcfProgress,
            language: language === 'en' ? 'en' : 'tr',
          };
          // Key derives from the upload's: a replayed upload never starts a
          // second analysis.
          const analysisResp = await transcriptionAPI.analyzeTranscript(
            response.document_id,
            { ...defaultOptions, idempotencyKey: derivedIdempotencyKey(transcriptionKey, 'analysis') },
          );
          nextAnalysisTaskId = analysisResp.task_id;
          nextAnalysisSessionId = analysisResp.session_id ?? nextAnalysisSessionId;
        } catch (autoErr) {
          // If auto-analysis kick-off fails, surface but don't block — the user
          // still has a successful transcript and can re-analyze manually.
          console.warn('Auto-analysis kick-off failed, continuing without it:', autoErr);
          // A spent card-backed trial analysis is not transient: say why no
          // analysis follows the (successful) transcript.
          const blocked = trialAnalysisUsedMessage(t, language, autoErr) || noCreditMessage(t, autoErr);
          if (blocked) showDialog(blocked);
        }
      }

      if (nextAnalysisTaskId && nextAnalysisSessionId) {
        activeAnalysisTaskId = nextAnalysisTaskId;
        // Hand off the modal: close TranscriptionProgress and open AnalysisProgress
        // so the user sees a single continuous flow (transcription → analysis).
        setShowTranscriptionProgress(false);
        // Audio flow drives its own completion/navigation inline below — keep
        // the modal's onComplete from also auto-opening/closing.
        analysisAutoOpenRef.current = false;
        analysisCompletionHandledRef.current = false;
        // This flow closes its modals on failure itself: no "Try again" here.
        analysisRetryRef.current = null;
        setAnalysisRetryAvailable(false);
        setAnalyzingDocumentName(pendingAudioFile.name);
        setAnalysisTaskId(nextAnalysisTaskId);
        setAnalysisSessionId(nextAnalysisSessionId);
        setShowAnalysisProgress(true);

        const pendingTask: PendingTask = {
          taskId: nextAnalysisTaskId,
          sessionId: nextAnalysisSessionId,
          kind: 'analysis',
          createdAt: Date.now(),
          filename: pendingAudioFile.name,
          instructions: analysisInstructionsForTranscription,
        };
        addPendingTask(pendingTask);
        setAnalyzingFile(true);
        const analysisResult = await waitForTaskResult<UnifiedAIResponse>(nextAnalysisTaskId);
        removePendingTask(nextAnalysisTaskId);
        setAnalyzingFile(false);

        const readySessionId = (analysisResult as any)?.session_id ?? nextAnalysisSessionId;
        markSessionAnalyzed(readySessionId);
        // Follow the analysis only if the user hasn't moved to another
        // conversation while it ran.
        if (
          guard.startingNew ||
          guard.viewSessionId == null ||
          guard.isViewing(nextAnalysisSessionId) ||
          guard.isViewing(readySessionId)
        ) {
          adoptSession(readySessionId);
        }
        await Promise.all([loadMessages(readySessionId), loadSessions(), loadQuotaStatus()]);
      }

      // Upload intent finished.
      transcriptionIntent.reset();
      // Show success and hide progress
      setTranscriptionStatus({ id: response.document_id, status: 'completed' });
      await Promise.all([loadTranscriptionUsage(), loadRecentDocuments()]);

      if (toolCall) {
        const extraResults = pendingToolResultsRef.current || [];
        const toolResultsPayload: ToolResult[] = [
          ...extraResults,
          {
            tool_call_id: toolCall.tool_call_id,
            tool_name: toolCall.tool_name,
            status: 'success',
            result: {
              document_id: response.document_id,
              task_id: response.task_id,
            },
          },
        ];

        pendingVoiceToolRef.current = null;
        pendingVoiceToolParamsRef.current = null;
        pendingToolResultsRef.current = [];

        void submitToolResults(toolResultsPayload, {
          originIntent,
          documentId: originIntent && ['analyze', 'hybrid'].includes(originIntent) ? response.document_id : undefined,
          analysisInstructions: mergedOptions.analysisInstructions,
          includeInICFProgress: mergedOptions.includeInICFProgress,
        });
      }

      // Brief delay to show completion. Per the new flow the user lands in the
      // chat session with the analysis side panel auto-opened — they can re-open
      // the transcript later via the "Transkripti aç" action.
      setTimeout(() => {
        setShowTranscriptionProgress(false);
        setShowAnalysisProgress(false);
        setAnalysisTaskId(null);
        setAnalysisSessionId(null);
        setAnalyzingDocumentName(null);
        setTranscriptionEstimate(null);
        setPendingAudioFile(null);
      }, 1200);

    } catch (error: any) {
      // Upload intent over (failed or cancelled).
      transcriptionIntent.reset();
      if (isTaskCancellationError(error, activeTranscriptionTaskId, activeAnalysisTaskId)) {
        setShowTranscriptionProgress(false);
        setShowAnalysisProgress(false);
        setTranscriptionTaskId(null);
        setTranscribingDocumentId(null);
        setAnalysisTaskId(null);
        setAnalysisSessionId(null);
        setAnalyzingDocumentName(null);
        setTranscriptionEstimate(null);
        setPendingAudioFile(null);
        if (toolCall) {
          const extraResults = pendingToolResultsRef.current || [];
          pendingVoiceToolRef.current = null;
          pendingVoiceToolParamsRef.current = null;
          pendingToolResultsRef.current = [];
          void submitToolResults([
            ...extraResults,
            {
              tool_call_id: toolCall.tool_call_id,
              tool_name: toolCall.tool_name,
              status: 'cancelled',
              result: { reason: 'user_cancelled' },
            },
          ], { originIntent });
        }
        return;
      }

      console.error('❌ Transcription failed:', error);
      const failure = transcriptionFailureEvent(error, 'chat');
      trackAuthEvent(failure.event, failure.properties);
      const friendlyMessage = trialAnalysisUsedMessage(t, language, error)
        || noCreditMessage(t, error)
        || voiceMinutesExceededMessage(t, error)
        || audioUploadRejectionMessage(t, error)
        || userFacingErrorMessage(t, error?.message || error);
      setTranscriptionStatus({
        id: transcribingDocumentId ?? 0,
        status: 'failed',
        error: friendlyMessage,
      });

      if (toolCall) {
        const extraResults = pendingToolResultsRef.current || [];
        const toolResultsPayload: ToolResult[] = [
          ...extraResults,
          {
            tool_call_id: toolCall.tool_call_id,
            tool_name: toolCall.tool_name,
            status: 'error',
            result: {
              error: friendlyMessage,
            },
          },
        ];
        pendingVoiceToolRef.current = null;
        pendingVoiceToolParamsRef.current = null;
        pendingToolResultsRef.current = [];
        void submitToolResults(toolResultsPayload, { originIntent });
      }

      // Keep showing error for a moment before hiding
      setTimeout(() => {
        setShowTranscriptionProgress(false);
        setShowAnalysisProgress(false);
        setAnalysisTaskId(null);
        setAnalysisSessionId(null);
        setAnalyzingDocumentName(null);
        setTranscriptionEstimate(null);
        setPendingAudioFile(null);
        showDialog(friendlyMessage);
      }, 2000);
    }
  };

  return {
    showTranscriptionModal,
    showTranscriptionProgress,
    pendingAudioFile,
    transcriptionStatus,
    transcriptionEstimate,
    transcriptionTaskId,
    handleAudioFileSelected,
    pickAudioForTranscription,
    handleTranscriptionCancelled,
    handleStartTranscription,
    handleTranscriptionProgressCancel,
  };
}
